let searchModule;
const loadSearch = () => searchModule ??= (window.importShim ?? ((specifier) => import(specifier)))('@theme/search')
  .catch(() => {
    searchModule = null;
    return null;
  });

const resetSearch = (region) => {
  if (typeof region?.reset === 'function') region.reset();
  else {
    const input = region?.querySelector('[data-search-input]');
    if (input) input.value = '';
  }
};

class DieuxHeader extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;

    this.abortController = new AbortController();
    const { signal } = this.abortController;
    const listen = (target, type, listener, options = {}) => {
      target?.addEventListener(type, listener, { ...options, signal });
    };

    const desktopHeader = this.querySelector('[data-desktop-header]');
    const desktopNavList = this.querySelector('[data-desktop-nav-list]');
    const desktopEnd = this.querySelector('[data-header-actions]');
    const megaLinks = [...this.querySelectorAll('[data-mega-link]')];
    const megaTriggers = [...this.querySelectorAll('[data-mega-trigger]')];
    const megaHoverTargets = [...this.querySelectorAll('[data-mega-hover-trigger]')];
    const megaPanels = [...this.querySelectorAll('[data-menu-panel]')];
    const mobileMenu = this.querySelector('[data-mobile-menu]');
    const mobileMenuTrigger = this.querySelector('[data-open-mobile-menu]');
    const mobileSiteHeader = this.querySelector('[data-mobile-site-header]');
    const mobileAccordions = [...this.querySelectorAll('[data-mobile-accordion]')];
    const fineHoverPointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    const reducedMotion = (window.DieuxFrame?.motionQuery ?? window.matchMedia('(prefers-reduced-motion: reduce)'));
    const timers = new Set();
    const megaPanelTimers = new WeakMap();
    const responsiveObservedSizes = new WeakMap();
    const backgroundInertState = new Map();

    let activeMega = null;
    let activeMegaTrigger = null;
    let activeMegaHoverTarget = null;
    let hoverOpenedMegaTrigger = null;
    let megaAimTriangle = null;
    let megaAimTimer = 0;
    let pendingMegaTrigger = null;
    let pendingHoverOpen = null;
    let lastDesktopPointerTarget = null;
    let desktopPointerTrail = [];
    let mobileMenuOpener = null;
    let mobileMenuTimer = 0;
    let headerArcsReady = !document.fonts;
    let scrollLockOwned = false;

    const desktopNavMinimumGap = 32;
    const desktopNavGapTolerance = 8;
    const headerArcMotionDuration = 200;
    const megaAimMaximumDuration = 300;
    const megaHoverRestDuration = 80;
    const megaHoverRestRadius = 6;
    const megaPanelMotionDuration = 300;
    const megaPanelCloseDelay = 200;
    const mobileMenuMotionDuration = 150;

    const later = (callback, delay) => {
      const timer = window.setTimeout(() => {
        timers.delete(timer);
        callback();
      }, delay);
      timers.add(timer);
      return timer;
    };

    const clearTimer = (timer) => {
      if (!timer) return;
      window.clearTimeout(timer);
      timers.delete(timer);
    };

    const isFineMousePointer = (event) => event.pointerType === 'mouse' && fineHoverPointer.matches;
    const isMobileMenuOpen = () => Boolean(mobileMenu && !mobileMenu.hidden);
    const hoverIntent = window.DieuxHeaderHover;
    const isParkedHoverTarget = (hoverTarget) => Boolean(hoverTarget && hoverIntent?.isParked(hoverTarget));
    const pageWrapper = this.closest('.page-wrapper');
    const mainContent = pageWrapper?.querySelector('#MainContent');
    const mobileMenuOffsetKey = Symbol('mobile-menu-offset');
    const syncMobileMenuOffset = () => {
      if (!isMobileMenuOpen()) return;
      window.DieuxFrame.measure(mobileMenuOffsetKey, () => {
        if (!this.isConnected || !isMobileMenuOpen()) return;
        const drawerTop = Math.max(0, this.getBoundingClientRect().top);
        window.DieuxFrame.write(() => {
          if (isMobileMenuOpen()) this.style.setProperty('--site-header-drawer-top', `${drawerTop}px`);
        });
      });
    };
    const syncOverlayNavigationScroll = () => {
      const scrollAtTop = String(window.scrollY <= 0);
      if (this.dataset.scrollAtTop !== scrollAtTop) this.dataset.scrollAtTop = scrollAtTop;
      syncMobileMenuOffset();
    };
    const headerGeometry = { header: -1, announcement: -1 };
    const headerGeometryKey = Symbol('header-geometry');
    const syncHeaderGeometry = () => {
      window.DieuxFrame.measure(headerGeometryKey, () => {
        if (!this.isConnected) return;

        const headerHeight = this.getBoundingClientRect().height;
        if (headerHeight <= 0) return;
        const announcementHeight =
          pageWrapper?.querySelector('.announcement-bar-shell')?.getBoundingClientRect().height || 0;
        if (headerHeight === headerGeometry.header && announcementHeight === headerGeometry.announcement) return;

        headerGeometry.header = headerHeight;
        headerGeometry.announcement = announcementHeight;

        window.DieuxFrame.write(() => {
          mainContent?.style.setProperty('--announcement-bar-height', `${announcementHeight}px`);
          mainContent?.style.setProperty('--header-height', `${headerHeight}px`);
          mainContent?.style.setProperty('--header-group-height', `${announcementHeight + headerHeight}px`);
        });
      });
    };
    const syncHeaderLayout = () => {
      syncHeaderGeometry();
      syncMobileMenuOffset();
    };
    const observeHeaderGeometry = () => {
      this.headerGeometryResizeObserver?.disconnect();
      this.headerGeometryResizeObserver = new ResizeObserver(syncHeaderLayout);
      this.headerGeometryResizeObserver.observe(this);

      const announcementBarShell = pageWrapper?.querySelector('.announcement-bar-shell');
      if (announcementBarShell) this.headerGeometryResizeObserver.observe(announcementBarShell);

      syncHeaderLayout();
    };

    listen(window, 'scroll', syncOverlayNavigationScroll, { passive: true });
    listen(document, 'shopify:section:load', observeHeaderGeometry);
    observeHeaderGeometry();
    syncOverlayNavigationScroll();
    syncHeaderGeometry();
    listen(window, 'pageshow', syncMobileMenuOffset);
    listen(window.visualViewport, 'resize', syncMobileMenuOffset, { passive: true });
    listen(window.visualViewport, 'scroll', syncMobileMenuOffset, { passive: true });

    const lastMenuPathSegment = (value) => {
      const parts = String(value || '').split('>');
      return parts[parts.length - 1].trim();
    };

    const navAbCandidateLinks = () => [...this.querySelectorAll('a[data-click-link-name]')].filter(
      (link) => !link.closest('dieux-search'),
    );

    const initializeNavAbTests = () => {
      const dataNode = this.querySelector('[data-nav-ab-tests]');
      if (!dataNode) return;

      let tests = [];
      try {
        tests = JSON.parse(dataNode.textContent || '[]');
      } catch (error) {
        console.warn('[nav-ab] Invalid header A/B test JSON.', error);
        return;
      }

      tests = tests.filter((test) => (
        test.title_key
        && test.variant_a_internal_title
        && test.variant_a_display_title
        && test.variant_a_url
        && test.variant_b_internal_title
        && test.variant_b_display_title
        && test.variant_b_url
      ));
      if (!tests.length) return;

      window.__navAbViewedQueue = window.__navAbViewedQueue || [];
      window.__navAbViewKeys = window.__navAbViewKeys || {};
      const seenTitleKeys = {};

      const readCookie = (name) => {
        const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const match = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`));
        return match ? decodeURIComponent(match[1]) : null;
      };

      const writeCookie = (name, value) => {
        document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${60 * 60 * 24 * 30}; SameSite=Lax`;
      };

      const pickVariant = (titleKey) => {
        const cookieKey = `ab_nav_${titleKey}`;
        const existing = readCookie(cookieKey);
        if (existing === 'A' || existing === 'B') return existing;
        const chosen = Math.random() < 0.5 ? 'A' : 'B';
        writeCookie(cookieKey, chosen);
        return chosen;
      };

      const swapLabelText = (link, oldLabel, newLabel) => {
        const walker = document.createTreeWalker(link, NodeFilter.SHOW_TEXT);
        let node = walker.nextNode();
        while (node) {
          const value = node.nodeValue || '';
          if (value.trim() === oldLabel) {
            node.nodeValue = value.replace(oldLabel, () => newLabel);
          }
          node = walker.nextNode();
        }
      };

      tests.forEach((test) => {
        if (seenTitleKeys[test.title_key]) {
          console.warn('[nav-ab] Duplicate title key skipped:', test.title_key);
          return;
        }
        seenTitleKeys[test.title_key] = true;

        const chosenSlot = pickVariant(test.title_key);
        const chosenInternal = chosenSlot === 'A'
          ? test.variant_a_internal_title
          : test.variant_b_internal_title;
        const displayTitle = chosenSlot === 'A'
          ? test.variant_a_display_title
          : test.variant_b_display_title;
        const displayUrl = chosenSlot === 'A' ? test.variant_a_url : test.variant_b_url;
        let matchCount = 0;

        navAbCandidateLinks().forEach((link) => {
          if (lastMenuPathSegment(link.dataset.clickLinkName) !== test.title_key) return;
          link.href = displayUrl;
          swapLabelText(link, test.title_key, displayTitle);
          link.dataset.ga4NavAb = '1';
          link.dataset.ga4NavTitle = test.title_key;
          link.dataset.ga4NavVariant = chosenInternal;
          matchCount += 1;
        });

        if (!matchCount) {
          console.warn('[nav-ab] No navigation links matched:', test.title_key);
          return;
        }

        const viewKey = `${test.title_key}:${chosenInternal}`;
        if (window.__navAbViewKeys[viewKey]) return;
        window.__navAbViewKeys[viewKey] = true;
        window.__navAbViewedQueue.push({
          nav_title: test.title_key,
          nav_variant: chosenInternal,
          match_count: matchCount,
        });
      });
    };

    initializeNavAbTests();

    const headerArcsKey = Symbol('header-arcs');
    const headerArcGroups = [...this.querySelectorAll('[data-header-arc-group]')].map((owner) => ({
      owner,
      controls: [...owner.querySelectorAll('[data-header-arc-target]')],
      lastTarget: null,
      revealKey: Symbol('header-arc-reveal'),
      revealPending: false,
      visible: false,
      hiddenAt: -Infinity,
    }));

    this.dataset.headerArcsReady = String(headerArcsReady);

    const preferredHeaderArcTarget = (group) => {
      const expandedTarget = group.controls.find((control) => {
        if (group.owner !== desktopNavList && !control.hasAttribute('data-header-arc-expanded')) {
          return false;
        }
        return (
          control.getAttribute('aria-expanded') === 'true'
          || control.querySelector('[aria-expanded="true"]')
        );
      });
      if (expandedTarget) return expandedTarget;
      const focusedTarget = group.controls.find((control) => (
        control.matches(':focus-visible') || control.querySelector(':focus-visible')
      ));
      if (focusedTarget) return focusedTarget;
      if (!fineHoverPointer.matches) return null;
      return group.controls.find((control) => control.matches(':hover')) || null;
    };

    const readHeaderArc = (group) => {
      const preferredTarget = preferredHeaderArcTarget(group);
      const target = preferredTarget || group.lastTarget || group.controls[0];
      const visible = headerArcsReady && Boolean(preferredTarget);

      if (!target?.getClientRects().length || !group.owner.getClientRects().length) {
        return { group, target, visible, placed: false, inlineStart: 0 };
      }

      const ownerBounds = group.owner.getBoundingClientRect();
      const targetBounds = target.getBoundingClientRect();
      const arcWidth = Number.parseFloat(getComputedStyle(group.owner, '::after').inlineSize);
      const targetInset = (targetBounds.width - arcWidth) / 2;
      const inlineStart = getComputedStyle(group.owner).direction === 'rtl'
        ? ownerBounds.right - targetBounds.right + targetInset
        : targetBounds.left - ownerBounds.left + targetInset;

      return { group, target, visible, placed: true, inlineStart };
    };

    const cancelHeaderArcReveal = (group) => {
      group.revealPending = false;
      window.DieuxFrame.cancelMeasure(group.revealKey);
    };

    const revealHeaderArc = (group, target) => {
      group.revealPending = true;
      window.DieuxFrame.measure(group.revealKey, () => {
        if (!headerArcsReady || preferredHeaderArcTarget(group) !== target) {
          group.revealPending = false;
          group.visible = false;
          scheduleHeaderArcs();
          return;
        }
        window.DieuxFrame.write(() => {
          if (!group.revealPending) return;
          group.revealPending = false;
          group.owner.style.setProperty('--header-arc-reveal-inset', '0.25em');
        });
      });
    };

    const applyHeaderArc = ({ group, target, visible, placed, inlineStart }) => {
      cancelHeaderArcReveal(group);

      if (!placed) {
        group.visible = false;
        group.hiddenAt = -Infinity;
        group.owner.style.setProperty('--header-arc-reveal-inset', '50%');
        return;
      }

      if (!visible) {
        group.lastTarget = target;
        if (group.visible) group.hiddenAt = performance.now();
        group.visible = false;
        group.owner.style.setProperty('--header-arc-inline-start', `${inlineStart}px`);
        group.owner.style.setProperty('--header-arc-reveal-inset', '50%');
        return;
      }

      if (!group.visible && performance.now() - group.hiddenAt >= headerArcMotionDuration) {
        group.lastTarget = target;
        group.visible = true;
        group.owner.dataset.headerArcSnapping = 'true';
        group.owner.style.setProperty('--header-arc-inline-start', `${inlineStart}px`);
        group.owner.style.setProperty('--header-arc-reveal-inset', '50%');
        getComputedStyle(group.owner, '::after').insetInlineStart;
        delete group.owner.dataset.headerArcSnapping;

        revealHeaderArc(group, target);
        return;
      }

      group.lastTarget = target;
      group.visible = true;
      group.owner.style.setProperty('--header-arc-inline-start', `${inlineStart}px`);
      group.owner.style.setProperty('--header-arc-reveal-inset', '0.25em');
    };

    const syncHeaderArcs = () => {
      const arcs = headerArcGroups.map(readHeaderArc);
      window.DieuxFrame.write(() => arcs.forEach(applyHeaderArc));
    };

    const scheduleHeaderArcs = () => {
      window.DieuxFrame.measure(headerArcsKey, syncHeaderArcs);
    };

    headerArcGroups.forEach((group) => {
      group.controls.forEach((control) => {
        listen(control, 'pointerenter', (event) => {
          if (isFineMousePointer(event)) scheduleHeaderArcs();
        });
        listen(control, 'focusin', scheduleHeaderArcs);
      });
      listen(group.owner, 'pointerleave', (event) => {
        if (isFineMousePointer(event)) scheduleHeaderArcs();
      });
      listen(group.owner, 'focusout', () => queueMicrotask(scheduleHeaderArcs));
    });

    const cancelMegaAim = () => {
      clearTimer(megaAimTimer);
      megaAimTimer = 0;
      megaAimTriangle = null;
      pendingMegaTrigger = null;
    };

    const cancelHoverOpen = () => {
      if (!pendingHoverOpen) return;
      window.cancelAnimationFrame(pendingHoverOpen.frame);
      pendingHoverOpen = null;
    };

    const settleHoverOpen = () => {
      const pending = pendingHoverOpen;
      if (!pending) return;
      const now = performance.now();
      const { points } = pending;
      const latest = points[points.length - 1];
      let reference = null;
      for (const point of points) {
        if (point.time > now - megaHoverRestDuration) break;
        reference = point;
      }
      const rested = latest
        ? Boolean(reference) && Math.hypot(latest.x - reference.x, latest.y - reference.y) <= megaHoverRestRadius
        : now - pending.startedAt >= megaHoverRestDuration;
      if (!rested) {
        pending.frame = window.requestAnimationFrame(settleHoverOpen);
        return;
      }
      pendingHoverOpen = null;
      if (!activeMega) openMega(pending.trigger, { source: 'hover' });
    };

    const beginHoverOpen = (hoverTarget, trigger, point) => {
      if (pendingHoverOpen?.hoverTarget === hoverTarget) return;
      cancelHoverOpen();
      const now = performance.now();
      pendingHoverOpen = {
        hoverTarget,
        trigger,
        startedAt: now,
        points: point ? [{ x: point.x, y: point.y, time: now }] : [],
        frame: window.requestAnimationFrame(settleHoverOpen),
      };
    };

    const trackHoverOpen = (point) => {
      const pending = pendingHoverOpen;
      if (!pending) return;
      const { points } = pending;
      points.push(point);
      while (points.length > 2 && points[1].time <= point.time - megaHoverRestDuration) points.shift();
    };

    const megaTriggerForNode = (node) => {
      if (!(node instanceof Element)) return null;
      const directTrigger = node.closest('[data-mega-trigger]');
      if (directTrigger) return directTrigger;
      return node.closest('[data-mega-hover-trigger]')?.querySelector('[data-mega-trigger]') || null;
    };

    const activeMegaContains = (node) => (
      node instanceof Node && Boolean(activeMega?.contains(node) || activeMegaHoverTarget?.contains(node))
    );
    const keyboardHoldsActiveMega = () => (
      document.documentElement.dataset.focusModality === 'keyboard'
      && activeMegaContains(document.activeElement)
    );
    const visibleMegaControls = (panel) => (
      panel
        ? [...panel.querySelectorAll('a[href], button:not([disabled])')]
          .filter((control) => control.getClientRects().length > 0)
        : []
    );

    const clearMegaPanelTimer = (panel) => {
      const timer = megaPanelTimers.get(panel);
      if (!timer) return;
      clearTimer(timer);
      megaPanelTimers.delete(panel);
    };

    const megaShellProperties = [
      '--site-header-mega-shell-from',
      '--site-header-mega-shell-offset',
      '--site-header-mega-shell-gradient-opacity',
      '--site-header-mega-shell-to',
    ];

    const clearMegaPanelSwap = (panel) => {
      delete panel.dataset.swap;
      megaShellProperties.forEach((property) => panel.style.removeProperty(property));
    };

    /**
     * Reads the visible shell of an open mega panel so the next panel can take it over in place.
     * @param {HTMLElement} panel
     * @returns {{ blockSize: number, offset: number, gradientOpacity: number } | null}
     */
    const readMegaPanelShell = (panel) => {
      const surface = panel.querySelector('[data-mega-surface]');
      if (!surface) return null;
      const { transform } = getComputedStyle(surface, '::before');
      return {
        blockSize: surface.getBoundingClientRect().height,
        offset: transform && transform !== 'none' ? new DOMMatrixReadOnly(transform).m42 : 0,
        gradientOpacity: Number(getComputedStyle(surface, '::after').opacity),
      };
    };

    const finishMegaPanelClose = (panel) => {
      clearMegaPanelTimer(panel);
      clearMegaPanelSwap(panel);
      panel.dataset.state = 'closed';
      panel.hidden = true;
    };

    /**
     * Shows a mega or search panel, taking over the outgoing panel's shell when one is passed.
     * @param {HTMLElement} panel
     * @param {{ shell?: { blockSize: number, offset: number, gradientOpacity: number } | null }} [options]
     * @returns {number | null} The panel's block size when it was laid out for the switch.
     */
    const showMegaPanel = (panel, { shell = null } = {}) => {
      clearMegaPanelTimer(panel);
      const wasHidden = panel.hidden;
      panel.hidden = false;
      panel.inert = false;

      if (reducedMotion.matches) {
        clearMegaPanelSwap(panel);
        panel.dataset.state = 'open';
        return null;
      }

      if (shell) {
        panel.dataset.swap = 'in';
        panel.style.setProperty('--site-header-mega-shell-from', `${shell.blockSize}px`);
        panel.style.setProperty('--site-header-mega-shell-offset', `${shell.offset}px`);
        panel.style.setProperty('--site-header-mega-shell-gradient-opacity', `${shell.gradientOpacity}`);
      } else {
        clearMegaPanelSwap(panel);
      }

      let blockSize = null;
      if (wasHidden || shell) {
        panel.dataset.state = 'closed';
        ({ height: blockSize } = panel.getBoundingClientRect());
      }
      panel.dataset.state = 'opening';
      const timer = later(() => {
        if (panel.dataset.state === 'opening') {
          panel.dataset.state = 'open';
          clearMegaPanelSwap(panel);
        }
        megaPanelTimers.delete(panel);
      }, megaPanelMotionDuration);
      megaPanelTimers.set(panel, timer);
      return blockSize;
    };

    const hideMegaPanel = (panel, { skipCloseHold = false, swap = false } = {}) => {
      clearMegaPanelTimer(panel);
      panel.inert = true;
      if (reducedMotion.matches) {
        finishMegaPanelClose(panel);
        return;
      }
      if (swap) {
        megaPanelTimers.set(panel, later(() => finishMegaPanelClose(panel), megaPanelMotionDuration));
        return;
      }
      clearMegaPanelSwap(panel);
      const closeHold = (skipCloseHold || panel.dataset.state === 'opening') ? 0 : megaPanelCloseDelay;
      this.style.setProperty('--site-header-panel-close-hold', `${closeHold}ms`);
      panel.dataset.state = 'closing';
      const timer = later(
        () => finishMegaPanelClose(panel),
        closeHold + megaPanelMotionDuration,
      );
      megaPanelTimers.set(panel, timer);
    };

    /**
     * Hands an outgoing mega panel's shell to the incoming one; runs after the incoming panel is laid out.
     * @param {HTMLElement} panel
     * @param {number} fromBlockSize The shell block size when the switch began.
     * @param {number} toBlockSize The incoming panel's block size.
     */
    const swapOutMegaPanel = (panel, fromBlockSize, toBlockSize) => {
      megaShellProperties.forEach((property) => panel.style.removeProperty(property));
      panel.style.setProperty('--site-header-mega-shell-from', `${fromBlockSize}px`);
      panel.style.setProperty('--site-header-mega-shell-to', `${toBlockSize}px`);
      panel.dataset.swap = 'out';
      panel.dataset.state = 'closing';
    };

    const closeMega = ({ restoreFocus = false, skipCloseHold = false, swap = false } = {}) => {
      cancelMegaAim();
      cancelHoverOpen();
      hoverOpenedMegaTrigger = null;
      if (!activeMega) {
        scheduleHeaderArcs();
        return;
      }
      const panel = activeMega;
      const trigger = activeMegaTrigger;
      hideMegaPanel(panel, { skipCloseHold, swap });
      trigger?.setAttribute('aria-expanded', 'false');
      activeMega = null;
      activeMegaTrigger = null;
      activeMegaHoverTarget = null;
      scheduleHeaderArcs();
      if (restoreFocus) trigger?.focus();
    };

    const searchPanel = this.querySelector('[data-search-panel]');
    const searchTrigger = this.querySelector('[data-search-trigger]');
    const desktopSearchRegion = searchPanel?.querySelector('dieux-search') || null;
    let searchOpen = false;

    const openSearch = ({ focusInput = true } = {}) => {
      if (!searchPanel || searchOpen) return;
      closeMega({ skipCloseHold: true });
      searchOpen = true;
      showMegaPanel(searchPanel);
      searchTrigger?.setAttribute('aria-expanded', 'true');
      scheduleHeaderArcs();
      loadSearch();
      if (focusInput) desktopSearchRegion?.querySelector('[data-search-input]')?.focus({ preventScroll: true });
    };

    const closeSearch = ({ restoreFocus = false } = {}) => {
      if (!searchPanel || !searchOpen) return;
      searchOpen = false;
      hideMegaPanel(searchPanel);
      searchTrigger?.setAttribute('aria-expanded', 'false');
      scheduleHeaderArcs();
      if (restoreFocus) searchTrigger?.focus();
      later(() => {
        if (!searchOpen) resetSearch(desktopSearchRegion);
      }, megaPanelMotionDuration);
    };

    this.querySelectorAll('[data-search-trigger], [data-open-mobile-search]').forEach((trigger) => {
      listen(trigger, 'pointerenter', loadSearch);
      listen(trigger, 'focus', loadSearch);
    });

    if (searchPanel && searchTrigger) {
      listen(searchTrigger, 'click', () => {
        if (searchOpen) closeSearch({ restoreFocus: true });
        else openSearch();
      });

      listen(searchPanel, 'keydown', (event) => {
        if (event.key !== 'Escape') return;
        if (desktopSearchRegion?.query) return;
        event.preventDefault();
        closeSearch({ restoreFocus: true });
      });

      this.querySelectorAll('[data-search-close]').forEach((button) => {
        listen(button, 'click', () => closeSearch({ restoreFocus: true }));
      });

      listen(document, 'pointerdown', (event) => {
        if (!searchOpen) return;
        const target = event.target;
        if (!(target instanceof Node)) return;
        if (searchPanel.contains(target) || searchTrigger.contains(target)) return;
        closeSearch();
      });

      listen(searchPanel, 'focusout', (event) => {
        if (!searchOpen) return;
        const next = event.relatedTarget;
        if (!(next instanceof Node)) return;
        if (searchPanel.contains(next) || searchTrigger.contains(next)) return;
        closeSearch();
      });
    }

    listen(this, 'click', (event) => {
      if (
        event.defaultPrevented
        || event.button !== 0
        || event.metaKey
        || event.ctrlKey
        || event.shiftKey
        || event.altKey
      ) {
        return;
      }

      const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (
        !link
        || !this.contains(link)
        || link.hasAttribute('download')
        || (link.target && link.target !== '_self')
      ) {
        return;
      }

      let destination = null;
      try {
        destination = new URL(link.href, window.location.href);
      } catch {
        destination = null;
      }
      const leavesPage = !destination
        || !destination.hash
        || destination.origin !== window.location.origin
        || destination.pathname !== window.location.pathname
        || destination.search !== window.location.search;

      if (activeMega && !(leavesPage && activeMegaContains(link))) closeMega({ skipCloseHold: true });
      if (searchOpen && !(leavesPage && searchPanel?.contains(link))) closeSearch();
      if (!leavesPage && mobileMenu?.contains(link)) closeMobileMenu({ restoreFocus: false });
    });

    const closeMegaFromPointer = () => {
      if (!keyboardHoldsActiveMega()) closeMega();
    };

    const speculativelyPrefetchedPanels = new WeakSet();
    const blockedSpeculationPath = /^\/(cart|checkout|checkouts|account|search|challenge|password|apps|tools|admin|discount)(\/|$)/;
    const accountRoutePath = /(^|\/)account(\/|$)/;
    const prefetchMegaPanelDestinations = (panel) => {
      if (accountRoutePath.test(window.location.pathname)) return;
      if (speculativelyPrefetchedPanels.has(panel)) return;
      speculativelyPrefetchedPanels.add(panel);

      const connection = navigator.connection;
      if (connection?.saveData || connection?.effectiveType?.includes('2g')) return;
      if (typeof HTMLScriptElement.supports !== 'function' || !HTMLScriptElement.supports('speculationrules')) return;

      const urls = [...new Set(
        [...panel.querySelectorAll('a[href]:not([target])')]
          .map((link) => {
            try {
              return new URL(link.href, window.location.href);
            } catch {
              return null;
            }
          })
          .filter((url) => (
            url
            && url.origin === window.location.origin
            && !url.hash
            && !blockedSpeculationPath.test(url.pathname)
            && url.pathname !== window.location.pathname
          ))
          .map((url) => url.href),
      )];
      if (!urls.length) return;

      const rule = document.createElement('script');
      rule.type = 'speculationrules';
      rule.textContent = JSON.stringify({
        prefetch: [{ source: 'list', urls, eagerness: 'eager' }],
      });
      document.head.append(rule);
    };

    const openMega = (trigger, { source = 'programmatic' } = {}) => {
      if (
        source === 'hover'
        && (keyboardHoldsActiveMega() || isParkedHoverTarget(trigger.closest('[data-mega-hover-trigger]')))
      ) {
        return;
      }
      const panel = this.querySelector(`#${CSS.escape(trigger.dataset.megaTrigger)}`);
      if (!panel) return;
      if (searchOpen) closeSearch();
      if (panel === activeMega) {
        if (source !== 'hover') hoverOpenedMegaTrigger = null;
        return;
      }
      const shell = activeMega && !reducedMotion.matches ? readMegaPanelShell(activeMega) : null;
      const outgoingPanel = shell ? activeMega : null;
      closeMega({ swap: Boolean(shell) });
      activeMega = panel;
      activeMegaTrigger = trigger;
      activeMegaHoverTarget = trigger.closest('[data-mega-hover-trigger]') || trigger;
      hoverOpenedMegaTrigger = source === 'hover' ? trigger : null;
      trigger.setAttribute('aria-expanded', 'true');
      const blockSize = showMegaPanel(panel, { shell });
      if (outgoingPanel) swapOutMegaPanel(outgoingPanel, shell.blockSize, blockSize ?? shell.blockSize);
      prefetchMegaPanelDestinations(panel);
      scheduleHeaderArcs();
    };

    const triangleContainsPoint = (point, triangle) => {
      const signedArea = (first, second, third) => (
        (first.x - third.x) * (second.y - third.y)
        - (second.x - third.x) * (first.y - third.y)
      );
      const first = signedArea(point, triangle.apex, triangle.left);
      const second = signedArea(point, triangle.left, triangle.right);
      const third = signedArea(point, triangle.right, triangle.apex);
      const hasNegative = first < 0 || second < 0 || third < 0;
      const hasPositive = first > 0 || second > 0 || third > 0;
      return !(hasNegative && hasPositive);
    };

    const createMegaAimTriangle = (event) => {
      if (!activeMega) return null;
      const currentPoint = { x: event.clientX, y: event.clientY };
      const previousPoint = [...desktopPointerTrail]
        .reverse()
        .find((point) => Math.hypot(point.x - currentPoint.x, point.y - currentPoint.y) >= 4);
      if (!previousPoint) return null;

      const deltaX = currentPoint.x - previousPoint.x;
      const deltaY = currentPoint.y - previousPoint.y;
      const panelBounds = activeMega.getBoundingClientRect();
      const corridorDestinationY = panelBounds.top + 12;
      const distanceToPanel = corridorDestinationY - currentPoint.y;
      if (deltaY <= 0 || distanceToPanel <= 0) return null;

      const projectedX = currentPoint.x + (deltaX / deltaY) * distanceToPanel;
      if (projectedX < panelBounds.left || projectedX > panelBounds.right) return null;
      const halfWidth = Math.min(144, Math.max(48, Math.abs(deltaX) + 48));
      return {
        apex: currentPoint,
        left: { x: Math.max(panelBounds.left, projectedX - halfWidth), y: corridorDestinationY },
        right: { x: Math.min(panelBounds.right, projectedX + halfWidth), y: corridorDestinationY },
        expiresAt: performance.now() + megaAimMaximumDuration,
      };
    };

    const resolveMegaAim = () => {
      const queuedTrigger = pendingMegaTrigger;
      const queuedHoverTarget = queuedTrigger?.closest('[data-mega-hover-trigger]');
      const pointerTarget = lastDesktopPointerTarget;
      cancelMegaAim();
      if (queuedHoverTarget?.matches(':hover')) {
        openMega(queuedTrigger, { source: 'hover' });
        return;
      }
      if (!activeMegaContains(pointerTarget)) closeMegaFromPointer();
    };

    const beginMegaAim = (event) => {
      const triangle = createMegaAimTriangle(event);
      if (!triangle) return false;
      cancelMegaAim();
      megaAimTriangle = triangle;
      megaAimTimer = later(resolveMegaAim, megaAimMaximumDuration);
      return true;
    };

    const hoverOntoTrigger = (trigger) => {
      if (isParkedHoverTarget(trigger.closest('[data-mega-hover-trigger]'))) closeMegaFromPointer();
      else openMega(trigger, { source: 'hover' });
    };

    const recordDesktopPointer = (event) => {
      if (!isFineMousePointer(event)) return;
      const now = performance.now();
      const point = { x: event.clientX, y: event.clientY, time: now };
      trackHoverOpen(point);
      lastDesktopPointerTarget = event.target;
      desktopPointerTrail.push(point);
      desktopPointerTrail = desktopPointerTrail
        .filter((entry) => now - entry.time <= 160)
        .slice(-6);

      if (megaAimTriangle) {
        if (activeMega?.contains(event.target)) {
          cancelMegaAim();
          return;
        }
        if (now <= megaAimTriangle.expiresAt && triangleContainsPoint(point, megaAimTriangle)) return;

        const enteredTrigger = megaTriggerForNode(event.target);
        cancelMegaAim();
        if (enteredTrigger && enteredTrigger !== activeMegaTrigger) {
          hoverOntoTrigger(enteredTrigger);
        } else if (!activeMegaContains(event.target)) {
          closeMegaFromPointer();
        }
        return;
      }

      if (activeMega && event.target.closest?.('[data-header-bar]') && !activeMegaContains(event.target)) {
        const enteredTrigger = megaTriggerForNode(event.target);
        if (enteredTrigger) hoverOntoTrigger(enteredTrigger);
        else closeMegaFromPointer();
      }
    };

    listen(desktopHeader, 'pointermove', recordDesktopPointer, { passive: true });

    megaHoverTargets.forEach((hoverTarget) => {
      const trigger = hoverTarget.querySelector('[data-mega-trigger]');
      if (!trigger) return;

      listen(hoverTarget, 'pointerenter', (event) => {
        if (!isFineMousePointer(event) || keyboardHoldsActiveMega()) return;
        if (isParkedHoverTarget(hoverTarget)) {
          if (activeMegaHoverTarget && activeMegaHoverTarget !== hoverTarget) closeMegaFromPointer();
          return;
        }
        if (
          megaAimTriangle
          && activeMegaTrigger !== trigger
          && triangleContainsPoint({ x: event.clientX, y: event.clientY }, megaAimTriangle)
        ) {
          pendingMegaTrigger = trigger;
          return;
        }
        cancelMegaAim();
        if (activeMega) openMega(trigger, { source: 'hover' });
        else beginHoverOpen(hoverTarget, trigger, { x: event.clientX, y: event.clientY });
      });

      listen(hoverTarget, 'pointerleave', (event) => {
        if (!isFineMousePointer(event)) return;
        if (pendingHoverOpen?.hoverTarget === hoverTarget) cancelHoverOpen();
        if (activeMegaHoverTarget !== hoverTarget) return;
        const nextTarget = event.relatedTarget;
        if (nextTarget instanceof Node && activeMega?.contains(nextTarget)) {
          cancelMegaAim();
          return;
        }
        if (beginMegaAim(event)) return;
        if (megaTriggerForNode(nextTarget)) return;
        closeMegaFromPointer();
      });
    });

    megaLinks.forEach((link) => {
      listen(link, 'pointerup', (event) => {
        if (event.pointerType !== 'touch' || !event.isPrimary) return;
        link.dataset.megaTouchToggle = 'true';
        later(() => link.removeAttribute('data-mega-touch-toggle'), 0);
      }, { passive: true });

      listen(link, 'pointercancel', () => {
        link.removeAttribute('data-mega-touch-toggle');
      }, { passive: true });

      listen(link, 'click', (event) => {
        const isDirectTouch = event.pointerType === 'touch' || link.dataset.megaTouchToggle === 'true';
        if (!isDirectTouch) return;

        event.preventDefault();
        cancelMegaAim();
        hoverOpenedMegaTrigger = null;
        const trigger = megaTriggerForNode(link);
        link.removeAttribute('data-mega-touch-toggle');
        if (!trigger) return;

        if (activeMegaTrigger === trigger) {
          closeMega();
          return;
        }

        openMega(trigger, { source: 'pointer' });
      });
    });

    megaTriggers.forEach((trigger) => {
      listen(trigger, 'click', (event) => {
        event.preventDefault();
        cancelMegaAim();
        hoverOpenedMegaTrigger = null;
        if (activeMegaTrigger === trigger) {
          closeMega();
          return;
        }

        openMega(trigger, { source: event.detail === 0 ? 'keyboard' : 'pointer' });
        if (event.detail === 0) {
          visibleMegaControls(activeMega)[0]?.focus();
        }
      });

      listen(trigger, 'keydown', (event) => {
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
        event.preventDefault();
        openMega(trigger, { source: 'keyboard' });
        const panelControls = visibleMegaControls(activeMega);
        const panelControl = event.key === 'ArrowUp' ? panelControls[panelControls.length - 1] : panelControls[0];
        panelControl?.focus();
      });
    });

    listen(desktopHeader, 'pointerleave', (event) => {
      if (!isFineMousePointer(event)) return;
      cancelMegaAim();
      closeMegaFromPointer();
    });

    listen(window, 'pageshow', (event) => {
      if (!event.persisted) return;
      desktopPointerTrail = [];
      lastDesktopPointerTarget = null;
      closeMega();
      megaPanels.forEach((panel) => finishMegaPanelClose(panel));
    });

    megaPanels.forEach((panel) => {
      panel.dataset.state = 'closed';
      panel.inert = true;
      panel.hidden = true;
      listen(panel, 'pointerenter', (event) => {
        if (isFineMousePointer(event)) cancelMegaAim();
      });
      listen(panel, 'pointerleave', (event) => {
        if (!isFineMousePointer(event)) return;
        const nextTarget = event.relatedTarget;
        if (nextTarget instanceof Node && activeMegaHoverTarget?.contains(nextTarget)) {
          cancelMegaAim();
          return;
        }
        if (megaTriggerForNode(nextTarget)) return;
        closeMegaFromPointer();
      });
      listen(panel, 'keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeMega({ restoreFocus: true });
          return;
        }
        if (event.key !== 'Tab') return;
        const controls = visibleMegaControls(panel);
        const firstControl = controls[0];
        const lastControl = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === firstControl) {
          event.preventDefault();
          activeMegaTrigger?.focus();
        } else if (!event.shiftKey && document.activeElement === lastControl) {
          event.preventDefault();
          const topControls = [...this.querySelectorAll('[data-header-bar] a[href], [data-header-bar] button:not([disabled])')];
          const triggerIndex = topControls.indexOf(activeMegaTrigger);
          closeMega();
          topControls[triggerIndex + 1]?.focus();
        }
      });
    });

    const mobileAccordionExpanded = (accordion) => (
      accordion.querySelector('[data-mobile-accordion-trigger]')?.getAttribute('aria-expanded') === 'true'
    );

    const setMobileAccordionExpanded = (accordion, expanded) => {
      const trigger = accordion.querySelector('[data-mobile-accordion-trigger]');
      const panel = accordion.querySelector('[data-mobile-accordion-panel]');
      if (!trigger || !panel) return;
      if (!expanded && panel.contains(document.activeElement)) trigger.focus({ preventScroll: true });
      trigger.setAttribute('aria-expanded', String(expanded));
      panel.inert = !expanded;
    };

    const setBackgroundInert = (inert) => {
      if (inert) {
        const headerGroup = this.closest('#header-group');
        const elements = [
          ...(headerGroup
            ? [...headerGroup.children].filter(
                (element) => !element.contains(this) && !element.querySelector('.announcement-bar-shell'),
              )
            : []),
          document.querySelector('.content-for-layout'),
          document.getElementById('footer-group'),
        ].filter(Boolean);
        elements.forEach((element) => {
          if (!backgroundInertState.has(element)) backgroundInertState.set(element, element.inert);
          element.inert = true;
        });
        return;
      }
      backgroundInertState.forEach((wasInert, element) => {
        if (element.isConnected) element.inert = wasInert;
      });
      backgroundInertState.clear();
    };

    const clearMobileMenuTimer = () => {
      clearTimer(mobileMenuTimer);
      mobileMenuTimer = 0;
    };

    const finishMobileMenuClose = ({ restoreFocus = true } = {}) => {
      clearMobileMenuTimer();
      mobileMenu.dataset.state = 'closed';
      mobileMenu.hidden = true;
      window.DieuxFrame.cancelMeasure(mobileMenuOffsetKey);
      this.style.removeProperty('--site-header-drawer-top');
      mobileSiteHeader.inert = false;
      setBackgroundInert(false);
      // A closed drawer must reopen on the navigation list, not stale results.
      resetSearch(mobileMenu.querySelector('dieux-search'));
      delete this.dataset.mobileSearchRequested;
      if (scrollLockOwned) {
        document.documentElement.removeAttribute('scroll-lock');
        scrollLockOwned = false;
      }
      if (restoreFocus && mobileMenuOpener?.isConnected) {
        mobileMenuOpener.focus({ preventScroll: true });
      }
    };

    const openMobileMenu = (opener = document.activeElement, { focus = true } = {}) => {
      if (!mobileMenu || !mobileMenuTrigger) return;
      loadSearch();
      closeMega();
      clearMobileMenuTimer();
      mobileMenuOpener = opener instanceof HTMLElement ? opener : mobileMenuTrigger;
      const wasHidden = mobileMenu.hidden;
      mobileMenu.hidden = false;
      mobileMenu.inert = false;
      syncMobileMenuOffset();
      mobileSiteHeader.inert = true;
      setBackgroundInert(true);
      mobileMenuTrigger.setAttribute('aria-expanded', 'true');
      if (!document.documentElement.hasAttribute('scroll-lock')) {
        document.documentElement.setAttribute('scroll-lock', '');
        scrollLockOwned = true;
      }

      if (reducedMotion.matches) {
        mobileMenu.dataset.state = 'open';
      } else {
        const beginOpeningMotion = () => {
          mobileMenu.dataset.state = 'opening';
          mobileMenuTimer = later(() => {
            if (mobileMenu.dataset.state === 'opening') mobileMenu.dataset.state = 'open';
            mobileMenuTimer = 0;
          }, mobileMenuMotionDuration);
        };

        if (!wasHidden) {
          beginOpeningMotion();
        } else {
          mobileMenu.dataset.state = 'closed';
          let openingMotionStarted = false;
          const startOpeningMotion = () => {
            if (openingMotionStarted) return;
            openingMotionStarted = true;
            if (mobileMenu.hidden || mobileMenu.dataset.state !== 'closed') return;
            beginOpeningMotion();
          };
          window.DieuxFrame.afterPaint(startOpeningMotion);
          later(startOpeningMotion, 300);
        }
      }
      if (focus) {
        window.DieuxFrame.afterPaint(() => {
          if (!isMobileMenuOpen() || mobileMenu.inert) return;
          mobileMenu.querySelector('[data-close-mobile-menu]')?.focus({ preventScroll: true });
        });
      }
    };

    const closeMobileMenu = ({ restoreFocus = true } = {}) => {
      if (!isMobileMenuOpen()) return;
      clearMobileMenuTimer();
      mobileMenu.inert = true;
      mobileMenuTrigger.setAttribute('aria-expanded', 'false');
      if (reducedMotion.matches) {
        finishMobileMenuClose({ restoreFocus });
        return;
      }
      mobileMenu.dataset.state = 'closing';
      mobileMenuTimer = later(
        () => finishMobileMenuClose({ restoreFocus }),
        mobileMenuMotionDuration,
      );
    };

    this.querySelectorAll('[data-open-mobile-menu]').forEach((button) => {
      listen(button, 'click', () => openMobileMenu(button));
    });
    this.querySelectorAll('[data-close-mobile-menu]').forEach((button) => {
      listen(button, 'click', () => closeMobileMenu());
    });

    const mobileSearchRegion = this.querySelector('[data-mobile-menu] dieux-search');
    this.querySelectorAll('[data-open-mobile-search]').forEach((button) => {
      listen(button, 'click', () => {
        this.dataset.mobileSearchRequested = 'true';
        if (!isMobileMenuOpen()) openMobileMenu(button, { focus: false });
        loadSearch();
        const input = mobileSearchRegion?.querySelector('[data-search-input]');
        input?.focus({ preventScroll: true });
        input?.select();
      });
    });

    let searchOpenRequested = false;
    try {
      searchOpenRequested = new URL(window.location.href).searchParams.get('search') === 'open';
    } catch {
      searchOpenRequested = false;
    }

    if (searchOpenRequested) {
      later(() => {
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete('search');
          window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
        } catch {
          // history can be unavailable in restricted browsing modes.
        }

        const isVisible = (element) => !!element && element.getClientRects().length > 0;

        if (isVisible(searchTrigger)) {
          openSearch();
          return;
        }

        const mobileSearchTriggers = [...this.querySelectorAll('[data-open-mobile-search]')];
        const mobileSearchTrigger = mobileSearchTriggers.find(isVisible) || mobileSearchTriggers[0];
        mobileSearchTrigger?.click();
      }, 0);
    }

    mobileAccordions.forEach((accordion) => {
      setMobileAccordionExpanded(accordion, false);
      const trigger = accordion.querySelector('[data-mobile-accordion-trigger]');
      listen(trigger, 'click', () => {
        const expanded = !mobileAccordionExpanded(accordion);
        if (expanded) {
          mobileAccordions.forEach((otherAccordion) => {
            if (otherAccordion !== accordion) setMobileAccordionExpanded(otherAccordion, false);
          });
        }
        setMobileAccordionExpanded(accordion, expanded);
        if (!expanded) return;
        const scroller = accordion.closest('.site-header__mobile-body');
        const item = accordion.closest('.site-header__mobile-item');
        if (scroller && item) scroller.scrollTop = Math.max(0, item.offsetTop - 65);
      });
    });

    listen(mobileMenu, 'keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        const openAccordion = mobileAccordions.find(mobileAccordionExpanded);
        if (openAccordion) setMobileAccordionExpanded(openAccordion, false);
        else closeMobileMenu();
        return;
      }
      if (event.key !== 'Tab') return;
      const controls = [...mobileMenu.querySelectorAll('a[href], button:not([disabled]), input:not([disabled])')]
        .filter((control) => control.getClientRects().length > 0 && !control.closest('[inert]'));
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    });

    listen(this, 'focusin', (event) => {
      if (activeMega && !activeMegaContains(event.target)) closeMega();
    });
    listen(this, 'focusout', (event) => {
      if (!activeMega || activeMegaContains(event.relatedTarget)) return;
      queueMicrotask(() => {
        if (!activeMega || activeMegaContains(document.activeElement)) return;

        const hiddenPanelControl = event.target instanceof HTMLElement
          && activeMega.contains(event.target)
          && event.target.getClientRects().length === 0;
        if (hiddenPanelControl) {
          const remainingControls = visibleMegaControls(activeMega);
          const fallbackControl = remainingControls[remainingControls.length - 1];
          if (fallbackControl) {
            fallbackControl.focus({ preventScroll: true });
            return;
          }
        }

        closeMega();
      });
    });
    listen(document, 'pointerdown', (event) => {
      if (activeMega && !activeMegaContains(event.target)) closeMega();
    });
    listen(document, 'keydown', (event) => {
      if (event.key === 'Escape' && activeMega) {
        event.preventDefault();
        closeMega({ restoreFocus: activeMegaContains(document.activeElement) });
        return;
      }
      if (
        event.key === 'Escape'
        && isMobileMenuOpen()
        && event.target instanceof Element
        && event.target.closest('.announcement-bar-shell')
      ) {
        event.preventDefault();
        closeMobileMenu();
      }
    });

    const setResponsiveLayout = (layout) => {
      if (this.dataset.layout === layout) return;
      const focusedElement = document.activeElement;
      const focusWasInDesktop = focusedElement instanceof Node && desktopHeader?.contains(focusedElement);
      const focusWasInMobile = focusedElement instanceof Node
        && (mobileSiteHeader?.contains(focusedElement) || mobileMenu?.contains(focusedElement));
      this.dataset.layout = layout;
      if (layout === 'mobile') {
        closeMega();
        if (focusWasInDesktop) mobileMenuTrigger?.focus({ preventScroll: true });
        return;
      }
      closeMobileMenu({ restoreFocus: false });
      if (focusWasInMobile) megaLinks[0]?.focus({ preventScroll: true });
    };

    const publishResponsiveLayout = (availableGap) => {
      this.dataset.responsiveGap = availableGap.toFixed(2);
      setResponsiveLayout(availableGap < desktopNavMinimumGap ? 'mobile' : 'desktop');
    };

    const responsiveGapModel = { offset: null, navItemCount: 0 };

    const observedInlineSize = (element) => responsiveObservedSizes.get(element);

    const observedNavItemsInlineSize = (navItems) => {
      let total = 0;
      for (const navItem of navItems) {
        const inlineSize = observedInlineSize(navItem);
        if (inlineSize === undefined) return null;
        total += inlineSize;
      }
      return total;
    };

    const calibrateResponsiveGapModel = (navItems, actionsWidth, availableGap) => {
      responsiveGapModel.offset = null;
      const headerSize = observedInlineSize(this);
      const actionsSize = observedInlineSize(desktopEnd);
      const navItemsSize = observedNavItemsInlineSize(navItems);
      if (headerSize === undefined || actionsSize === undefined || navItemsSize === null) return;
      if (actionsWidth <= 0 || Math.abs(actionsSize - actionsWidth) > 0.5) return;
      responsiveGapModel.offset = headerSize - navItemsSize - actionsSize - availableGap;
      responsiveGapModel.navItemCount = navItems.length;
    };

    const estimateResponsiveGap = (navItems) => {
      if (responsiveGapModel.offset === null) return null;
      if (responsiveGapModel.navItemCount !== navItems.length) return null;
      const headerSize = observedInlineSize(this);
      const actionsSize = observedInlineSize(desktopEnd);
      const navItemsSize = observedNavItemsInlineSize(navItems);
      if (headerSize === undefined || actionsSize === undefined || navItemsSize === null) return null;
      return headerSize - navItemsSize - actionsSize - responsiveGapModel.offset;
    };

    const measureResponsiveGap = (lastLeftMenuItem, navItems) => {
      this.dataset.responsiveMeasuring = 'true';
      const leftMenuBounds = lastLeftMenuItem.getBoundingClientRect();
      const rightMenuBounds = desktopEnd.getBoundingClientRect();
      delete this.dataset.responsiveMeasuring;
      const availableGap = rightMenuBounds.left - leftMenuBounds.right;
      calibrateResponsiveGapModel(navItems, rightMenuBounds.width, availableGap);
      return availableGap;
    };

    const measureResponsiveLayout = () => {
      if (!desktopNavList || !desktopEnd) return;
      if (getComputedStyle(this).getPropertyValue('--site-header-layout').trim() === 'mobile') {
        window.DieuxFrame.write(() => setResponsiveLayout('mobile'));
        return;
      }
      const lastLeftMenuItem = desktopNavList.lastElementChild;
      if (!lastLeftMenuItem) {
        window.DieuxFrame.write(() => setResponsiveLayout('desktop'));
        return;
      }

      const navItems = [...desktopNavList.children];
      const estimatedGap = this.dataset.layout === 'desktop' ? estimateResponsiveGap(navItems) : null;
      if (estimatedGap !== null && Math.abs(estimatedGap - desktopNavMinimumGap) > desktopNavGapTolerance) {
        window.DieuxFrame.write(() => publishResponsiveLayout(estimatedGap));
        return;
      }

      window.DieuxFrame.write(() => {
        publishResponsiveLayout(measureResponsiveGap(lastLeftMenuItem, navItems));
      });
    };

    const responsiveLayoutKey = Symbol('header-responsive-layout');
    const scheduleResponsiveLayout = () => {
      scheduleHeaderArcs();
      window.DieuxFrame.measure(responsiveLayoutKey, measureResponsiveLayout);
    };

    if (desktopNavList && desktopEnd) {
      this.responsiveLayoutObserver = new ResizeObserver((entries) => {
        let changed = false;
        entries.forEach((entry) => {
          const inlineSize = entry.contentBoxSize?.[0]?.inlineSize ?? entry.contentRect.width;
          const previousSize = responsiveObservedSizes.get(entry.target);
          responsiveObservedSizes.set(entry.target, inlineSize);
          if (previousSize === undefined || Math.abs(inlineSize - previousSize) >= 0.25) changed = true;
        });
        if (changed) scheduleResponsiveLayout();
      });
      this.responsiveLayoutObserver.observe(this);
      [...desktopNavList.children, desktopEnd].forEach((element) => {
        this.responsiveLayoutObserver.observe(element);
      });
    }

    if (desktopNavList && desktopEnd) {
      this.responsiveContentObserver = new MutationObserver(scheduleResponsiveLayout);
      const responsiveContentObserverOptions = {
        childList: true,
        characterData: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style', 'hidden'],
      };
      this.responsiveContentObserver.observe(desktopNavList, responsiveContentObserverOptions);
      this.responsiveContentObserver.observe(desktopEnd, responsiveContentObserverOptions);
    }

    if (document.fonts) {
      document.fonts.ready.then(() => {
        if (!this.isConnected) return;
        headerArcsReady = true;
        this.dataset.headerArcsReady = 'true';
        this.dataset.headerArcsPriming = 'true';
        this.getBoundingClientRect();
        window.requestAnimationFrame(() => {
          if (!this.isConnected) return;
          delete this.dataset.headerArcsPriming;
          scheduleResponsiveLayout();
        });
      });
      listen(document.fonts, 'loadingdone', scheduleResponsiveLayout);
    }

    scheduleResponsiveLayout();

    let editorRevealedMega = null;
    let editorRevealedMobileMenu = false;

    const isEditorTargetVisible = (target) => {
      const bounds = target.getBoundingClientRect();
      return bounds.width > 0 && bounds.height > 0 && !target.closest('[inert]');
    };

    const navAbEditorTargets = (marker) => {
      const titleKey = marker.dataset.headerNavAbTitleKey;
      if (!titleKey) return [];
      return navAbCandidateLinks().filter(
        (link) => lastMenuPathSegment(link.dataset.clickLinkName) === titleKey,
      );
    };

    const revealEditorTarget = (target) => {
      const panel = target.closest('[data-menu-panel]');
      if (panel) {
        if (this.dataset.layout !== 'desktop') return false;
        if (panel !== activeMega) {
          const trigger = megaTriggers.find((candidate) => candidate.dataset.megaTrigger === panel.id);
          if (!trigger) return false;
          openMega(trigger, { source: 'programmatic' });
          editorRevealedMega = panel;
        }
        return true;
      }

      if (mobileMenu?.contains(target)) {
        if (this.dataset.layout !== 'mobile') return false;
        if (!isMobileMenuOpen()) {
          openMobileMenu(mobileMenuTrigger, { focus: false });
          editorRevealedMobileMenu = true;
        }
        const accordion = target.closest('[data-mobile-accordion]');
        if (accordion && !mobileAccordionExpanded(accordion)) {
          mobileAccordions.forEach((otherAccordion) => {
            if (otherAccordion !== accordion) setMobileAccordionExpanded(otherAccordion, false);
          });
          setMobileAccordionExpanded(accordion, true);
        }
        if (target.closest('.site-header__mobile-body')) {
          later(() => target.scrollIntoView({ block: 'nearest' }), 0);
        }
        return true;
      }

      return isEditorTargetVisible(target);
    };

    this.selectBlock = (blockId) => {
      if (!blockId) return false;
      const occurrences = [...this.querySelectorAll(`[data-header-block-id="${CSS.escape(blockId)}"]`)];
      const targets = occurrences.flatMap((occurrence) => (
        occurrence.dataset.headerNavAbTitleKey === undefined ? [occurrence] : navAbEditorTargets(occurrence)
      ));
      if (targets.some(isEditorTargetVisible)) return true;
      return targets.some((target) => revealEditorTarget(target));
    };

    this.releaseEditorSelection = () => {
      if (editorRevealedMega && editorRevealedMega === activeMega) closeMega();
      editorRevealedMega = null;
      if (editorRevealedMobileMenu && isMobileMenuOpen()) closeMobileMenu({ restoreFocus: false });
      editorRevealedMobileMenu = false;
    };

    this.cleanupHeader = () => {
      closeMega();
      megaPanels.forEach((panel) => finishMegaPanelClose(panel));
      if (isMobileMenuOpen()) finishMobileMenuClose({ restoreFocus: false });
      timers.forEach((timer) => window.clearTimeout(timer));
      timers.clear();
      megaLinks.forEach((link) => link.removeAttribute('data-mega-touch-toggle'));
      headerArcGroups.forEach(cancelHeaderArcReveal);
      this.responsiveLayoutObserver?.disconnect();
      this.responsiveContentObserver?.disconnect();
      this.headerGeometryResizeObserver?.disconnect();
      window.DieuxFrame.cancelMeasure(headerArcsKey);
      window.DieuxFrame.cancelMeasure(responsiveLayoutKey);
      window.DieuxFrame.cancelMeasure(headerGeometryKey);
      window.DieuxFrame.cancelMeasure(mobileMenuOffsetKey);
      mainContent?.style.removeProperty('--announcement-bar-height');
      mainContent?.style.removeProperty('--header-height');
      mainContent?.style.removeProperty('--header-group-height');
      this.style.removeProperty('--site-header-drawer-top');
      setBackgroundInert(false);
    };

    const ownsInitialTarget = (hoverTarget) => Boolean(hoverTarget?.isConnected && this.contains(hoverTarget));
    const clickedHoverTarget = hoverIntent?.takeClick();
    const hoveredTarget = hoverIntent?.current();
    if (!isMobileMenuOpen()) {
      if (ownsInitialTarget(clickedHoverTarget)) {
        const trigger = clickedHoverTarget.querySelector('[data-mega-trigger]');
        if (trigger) openMega(trigger, { source: 'programmatic' });
      } else if (fineHoverPointer.matches && ownsInitialTarget(hoveredTarget) && hoveredTarget.matches(':hover')) {
        const trigger = hoveredTarget.querySelector('[data-mega-trigger]');
        if (trigger && !isParkedHoverTarget(hoveredTarget)) beginHoverOpen(hoveredTarget, trigger);
      }
    }
  }

  disconnectedCallback() {
    this.cleanupHeader?.();
    this.abortController?.abort();
    this.abortController = null;
    this.cleanupHeader = null;
    this.selectBlock = null;
    this.releaseEditorSelection = null;
  }
}

if (!customElements.get('dieux-header')) {
  customElements.define('dieux-header', DieuxHeader);
}
